import { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { UserPlus, KeyRound, Trash2, ShieldCheck, User, Wand2, Copy, Power, Check, LockOpen, Search } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { generatePassword } from "@/lib/password";
import { formatWib, toWibInput, addMonthsIso } from "@/lib/datetime";
import { AccessFields, accessPayload, EditAccessDialog, RenewDialog, StatusBadges, RemainingDays } from "@/components/AdminAccess";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

// Default period for new customer accounts: starts now, ends 12 months later (editable / clearable).
const defaultAccess = () => {
  const nowIso = new Date().toISOString();
  return { start: toWibInput(nowIso), expires: toWibInput(addMonthsIso(nowIso, 12)), note: "" };
};

function CreateUserDialog({ onDone, onShowCred }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "user" });
  const [access, setAccess] = useState(defaultAccess);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onOpenChange = (o) => {
    if (o) setAccess(defaultAccess()); // fresh "now" each time the dialog opens
    setOpen(o);
  };

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.password) return toast.error("Lengkapi semua kolom.");
    if (form.password.length < 8) return toast.error("Kata sandi minimal 8 karakter.");
    let payload = { ...form };
    if (form.role === "user") {
      const p = accessPayload(access);
      if (p.error) return toast.error(p.error);
      payload = { ...payload, ...p };
    }
    try {
      await api.post("/admin/users", payload);
      toast.success("Akun pelanggan dibuat.");
      onShowCred({ email: form.email.trim(), password: form.password });
      setOpen(false); setForm({ name: "", email: "", password: "", role: "user" });
      onDone();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button className="rounded-xl min-h-[44px]" data-testid="admin-create-user-button"><UserPlus className="mr-2 h-4 w-4" /> Buat Akun Baru</Button>
      </DialogTrigger>
      <DialogContent className="rounded-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Buat Akun Pelanggan</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Nama</Label>
            <Input value={form.name} onChange={(e) => set("name", e.target.value)} className="rounded-xl" data-testid="admin-user-name-input" /></div>
          <div className="space-y-1.5"><Label>Email</Label>
            <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="rounded-xl" data-testid="admin-user-email-input" /></div>
          <div className="space-y-1.5"><Label>Kata Sandi Sementara</Label>
            <div className="flex gap-2">
              <Input value={form.password} onChange={(e) => set("password", e.target.value)} className="rounded-xl font-mono" placeholder="min. 8 karakter" data-testid="admin-user-password-input" />
              <Button type="button" variant="secondary" className="rounded-xl shrink-0" onClick={() => set("password", generatePassword())} data-testid="admin-generate-password-button"><Wand2 className="mr-1.5 h-4 w-4" /> Buat Otomatis</Button>
            </div>
            <p className="text-xs text-muted-foreground">Pelanggan wajib mengganti kata sandi ini saat pertama kali masuk.</p>
          </div>
          <div className="space-y-1.5"><Label>Peran</Label>
            <Select value={form.role} onValueChange={(v) => set("role", v)}>
              <SelectTrigger className="rounded-xl" data-testid="admin-user-role-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="user">Pelanggan</SelectItem><SelectItem value="admin">Admin</SelectItem></SelectContent>
            </Select>
          </div>
          {form.role === "user" ? (
            <AccessFields value={access} onChange={setAccess} testPrefix="admin-create-access" />
          ) : (
            <p className="text-xs text-muted-foreground">Akun admin tidak memiliki batas masa aktif.</p>
          )}
        </div>
        <DialogFooter><Button onClick={submit} className="rounded-xl" data-testid="admin-user-submit-button">Buat Akun</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResetDialog({ user, onShowCred }) {
  const [open, setOpen] = useState(false);
  const [pw, setPw] = useState("");
  const submit = async () => {
    if (!pw || pw.length < 8) return toast.error("Kata sandi minimal 8 karakter.");
    try {
      await api.post(`/admin/users/${user.user_id}/reset-password`, { password: pw });
      toast.success("Kata sandi direset.");
      onShowCred({ email: user.email, password: pw });
      setOpen(false); setPw("");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="text-muted-foreground hover:text-primary" title="Reset kata sandi" data-testid={`admin-reset-${user.user_id}`}><KeyRound className="h-4 w-4" /></button>
      </DialogTrigger>
      <DialogContent className="rounded-2xl">
        <DialogHeader><DialogTitle>Reset Kata Sandi — {user.name}</DialogTitle></DialogHeader>
        <div className="space-y-1.5"><Label>Kata Sandi Sementara Baru</Label>
          <div className="flex gap-2">
            <Input value={pw} onChange={(e) => setPw(e.target.value)} className="rounded-xl font-mono" placeholder="min. 8 karakter" data-testid="admin-reset-password-input" />
            <Button type="button" variant="secondary" className="rounded-xl shrink-0" onClick={() => setPw(generatePassword())} data-testid="admin-reset-generate-button"><Wand2 className="mr-1.5 h-4 w-4" /> Buat Otomatis</Button>
          </div>
          <p className="text-xs text-muted-foreground">Pengguna wajib mengganti kata sandi ini saat login berikutnya.</p>
        </div>
        <DialogFooter><Button onClick={submit} className="rounded-xl" data-testid="admin-reset-submit-button">Reset & Wajib Ganti</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ShowCredDialog({ cred, onClose }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(cred.password); } catch {}
    setCopied(true); setTimeout(() => setCopied(false), 2000);
  };
  return (
    <Dialog open={!!cred} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="rounded-2xl" data-testid="admin-cred-dialog">
        <DialogHeader><DialogTitle>Kata Sandi Sementara</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">Salin dan kirim ke pelanggan sekarang. Kata sandi ini <b>hanya ditampilkan sekali</b> dan tidak disimpan dalam bentuk teks.</p>
          <div className="rounded-xl border border-border bg-secondary/50 p-3">
            <p className="text-xs text-muted-foreground">{cred.email}</p>
            <p className="font-mono text-lg font-bold" data-testid="admin-cred-password">{cred.password}</p>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={copy} className="rounded-xl" data-testid="admin-cred-copy-button">
            {copied ? <><Check className="mr-1.5 h-4 w-4" /> Tersalin</> : <><Copy className="mr-1.5 h-4 w-4" /> Salin Kata Sandi</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Admin() {
  const qc = useQueryClient();
  const { user: me } = useAuth();
  const [cred, setCred] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("semua");
  const [expiryFilter, setExpiryFilter] = useState("semua");
  const [sortBy, setSortBy] = useState("terbaru");
  const { data: users = [], isLoading, isError, error } = useQuery({ queryKey: ["admin-users"], queryFn: async () => (await api.get("/admin/users")).data });
  const refetch = () => qc.invalidateQueries({ queryKey: ["admin-users"] });

  const del = async (id) => {
    try { await api.delete(`/admin/users/${id}`); toast.success("Akun dihapus."); refetch(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const toggleStatus = async (u) => {
    try {
      await api.post(`/admin/users/${u.user_id}/status`, { disabled: !u.disabled });
      toast.success(u.disabled ? "Akun diaktifkan." : "Akun dinonaktifkan.");
      refetch();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const unlock = async (u) => {
    try {
      await api.post(`/admin/users/${u.user_id}/unlock`);
      toast.success(`Kunci akun "${u.email}" dibuka.`);
      refetch();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const now = Date.now();
    let list = users.filter((u) => {
      if (q && ![u.name, u.business_name, u.email].some((v) => (v || "").toLowerCase().includes(q))) return false;
      const st = u.status || (u.disabled ? "nonaktif" : "aktif");
      if (statusFilter === "terkunci" && !u.locked) return false;
      if (statusFilter === "aktif" && (st !== "aktif" || u.locked)) return false;
      if (!["semua", "terkunci", "aktif"].includes(statusFilter) && st !== statusFilter) return false;
      if (expiryFilter !== "semua") {
        if (u.role === "admin" || !u.access_expires_at) return false;
        const ms = new Date(u.access_expires_at).getTime() - now;
        if (!(ms > 0 && ms <= Number(expiryFilter) * 86400000)) return false;
      }
      return true;
    });
    if (sortBy === "kedaluwarsa") {
      const key = (u) => (u.role !== "admin" && u.access_expires_at ? new Date(u.access_expires_at).getTime() : Infinity);
      list = [...list].sort((a, b) => key(a) - key(b));
    }
    return list;
  }, [users, search, statusFilter, expiryFilter, sortBy]);

  const actions = (u, wrap = "flex-wrap") => (
    <div className={`flex ${wrap} items-center justify-end gap-3`}>
      {u.role !== "admin" && <RenewDialog user={u} onDone={refetch} />}
      {u.role !== "admin" && <EditAccessDialog user={u} onDone={refetch} />}
      <ResetDialog user={u} onShowCred={setCred} />
      <button onClick={() => unlock(u)} title="Buka kunci akun"
        className="text-muted-foreground hover:text-primary"
        data-testid={`admin-unlock-${u.user_id}`}><LockOpen className="h-4 w-4" /></button>
      {u.user_id !== me?.user_id && (
        <button onClick={() => toggleStatus(u)} title={u.disabled ? "Aktifkan akun" : "Nonaktifkan akun"}
          className={`hover:opacity-80 ${u.disabled ? "text-primary" : "text-amber-600 dark:text-amber-400"}`}
          data-testid={`admin-toggle-status-${u.user_id}`}><Power className="h-4 w-4" /></button>
      )}
      {u.user_id !== me?.user_id && (
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <button className="text-muted-foreground hover:text-destructive" data-testid={`admin-delete-${u.user_id}`}><Trash2 className="h-4 w-4" /></button>
          </AlertDialogTrigger>
          <AlertDialogContent className="rounded-2xl">
            <AlertDialogHeader><AlertDialogTitle>Hapus akun?</AlertDialogTitle>
              <AlertDialogDescription>Akun "{u.email}" beserta datanya tidak akan bisa login lagi.</AlertDialogDescription></AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-xl">Batal</AlertDialogCancel>
              <AlertDialogAction className="rounded-xl bg-destructive hover:bg-destructive/90" onClick={() => del(u.user_id)}>Hapus</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );

  const roleBadge = (u) => u.role === "admin" ? (
    <Badge className="rounded-md gap-1"><ShieldCheck className="h-3 w-3" /> Admin</Badge>
  ) : (
    <Badge variant="secondary" className="rounded-md gap-1"><User className="h-3 w-3" /> Pelanggan</Badge>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">Panel Admin</h1>
          <p className="text-sm text-muted-foreground">Kelola akun pelanggan berbayar: buat akun, masa aktif & perpanjangan, reset kata sandi, aktif/nonaktif.</p>
        </div>
        <CreateUserDialog onDone={refetch} onShowCred={setCred} />
      </div>

      <Card className="rounded-2xl p-3 sm:p-4">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="relative sm:col-span-2 lg:col-span-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari bisnis, nama, atau email" className="rounded-xl pl-9" data-testid="admin-search-input" />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="rounded-xl" data-testid="admin-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="semua">Semua status</SelectItem>
              <SelectItem value="aktif">Aktif</SelectItem>
              <SelectItem value="terkunci">Terkunci</SelectItem>
              <SelectItem value="nonaktif">Nonaktif</SelectItem>
              <SelectItem value="kedaluwarsa">Kedaluwarsa</SelectItem>
              <SelectItem value="belum_dimulai">Belum Dimulai</SelectItem>
            </SelectContent>
          </Select>
          <Select value={expiryFilter} onValueChange={setExpiryFilter}>
            <SelectTrigger className="rounded-xl" data-testid="admin-expiry-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="semua">Semua masa aktif</SelectItem>
              <SelectItem value="7">Berakhir dalam 7 hari</SelectItem>
              <SelectItem value="30">Berakhir dalam 30 hari</SelectItem>
            </SelectContent>
          </Select>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="rounded-xl" data-testid="admin-sort-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="terbaru">Urutkan: Terbaru dibuat</SelectItem>
              <SelectItem value="kedaluwarsa">Urutkan: Kedaluwarsa terdekat</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <p className="mt-3 text-xs text-muted-foreground" data-testid="admin-result-count">
          Menampilkan {filtered.length} dari {users.length} akun · Waktu ditampilkan dalam WIB (UTC+7)
        </p>
      </Card>

      {isError && (
        <Card className="rounded-2xl border-destructive/40 p-4 text-sm text-destructive" data-testid="admin-load-error">
          Gagal memuat daftar akun: {apiError(error?.response?.data?.detail)}
        </Card>
      )}

      {/* Desktop / tablet: table */}
      <Card className="hidden overflow-hidden rounded-2xl md:block">
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary/60">
              <TableHead>Nama / Bisnis</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Peran</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Mulai Aktif</TableHead>
              <TableHead>Berakhir</TableHead>
              <TableHead>Sisa</TableHead>
              <TableHead className="text-right">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((u) => (
              <TableRow key={u.user_id} data-testid={`admin-user-row-${u.user_id}`}>
                <TableCell>
                  <p className="font-medium">{u.name}</p>
                  {u.business_name && <p className="text-xs text-muted-foreground">{u.business_name}</p>}
                  {u.admin_note && <p className="mt-0.5 max-w-[220px] truncate text-xs italic text-muted-foreground" title={u.admin_note}>{u.admin_note}</p>}
                </TableCell>
                <TableCell className="text-muted-foreground">{u.email}</TableCell>
                <TableCell>{roleBadge(u)}</TableCell>
                <TableCell><StatusBadges u={u} /></TableCell>
                <TableCell className="whitespace-nowrap text-xs">{formatWib(u.access_start_at)}</TableCell>
                <TableCell className="whitespace-nowrap text-xs" data-testid={`admin-expires-${u.user_id}`}>{formatWib(u.access_expires_at)}</TableCell>
                <TableCell className="whitespace-nowrap"><RemainingDays u={u} /></TableCell>
                <TableCell>{actions(u, "flex-nowrap")}</TableCell>
              </TableRow>
            ))}
            {!isLoading && filtered.length === 0 && (
              <TableRow><TableCell colSpan={8} className="py-8 text-center text-sm text-muted-foreground">Tidak ada akun yang cocok.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Mobile: cards */}
      <div className="space-y-3 md:hidden">
        {filtered.map((u) => (
          <Card key={u.user_id} className="rounded-2xl p-4" data-testid={`admin-user-card-${u.user_id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="font-medium">{u.name}</p>
                {u.business_name && <p className="text-xs text-muted-foreground">{u.business_name}</p>}
                <p className="break-all text-sm text-muted-foreground">{u.email}</p>
              </div>
              {roleBadge(u)}
            </div>
            <div className="mt-3"><StatusBadges u={u} /></div>
            <dl className="mt-3 grid grid-cols-3 gap-2 text-xs">
              <div><dt className="text-muted-foreground">Mulai</dt><dd>{formatWib(u.access_start_at, { dateOnly: true })}</dd></div>
              <div><dt className="text-muted-foreground">Berakhir</dt><dd>{formatWib(u.access_expires_at, { dateOnly: true })}</dd></div>
              <div><dt className="text-muted-foreground">Sisa</dt><dd><RemainingDays u={u} /></dd></div>
            </dl>
            {u.admin_note && <p className="mt-2 text-xs italic text-muted-foreground">{u.admin_note}</p>}
            <div className="mt-3 border-t border-border pt-3">{actions(u)}</div>
          </Card>
        ))}
        {!isLoading && filtered.length === 0 && (
          <p className="py-6 text-center text-sm text-muted-foreground">Tidak ada akun yang cocok.</p>
        )}
      </div>

      {cred && <ShowCredDialog cred={cred} onClose={() => setCred(null)} />}
    </div>
  );
}
